import React, { useMemo } from "react";
import NotificationPanel from "./NotificationPanel";
import axios from "axios";
import { SERVER_URL } from "../config";
import { useNavigate, useLocation } from "react-router-dom";
import {
  XMarkIcon,
  HomeModernIcon,
  ClockIcon,
  BookmarkIcon,
  ArrowLeftOnRectangleIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  PresentationChartLineIcon,
  DocumentTextIcon,
  BellIcon
} from "@heroicons/react/24/outline";

const Sidebar = ({ onLogout, isOpen = false, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  // ✅ Safe employee parse
  const employee = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("employee")) || {};
    } catch {
      return {};
    }
  }, []);
  const employeeId = employee?.employee_id;

  const fetchUnread = async () => {
    if (!employeeId) return;

    try {
      const res = await axios.get(
        `${SERVER_URL}/notification_helper.php`,
        {
          params: {
            action: "getUnreadCount",
            employee_id: employeeId,
          },
        }
      );

      if (res.data?.status) {
        setUnreadCount(res.data.count || 0);
      }
    } catch (err) {
      console.error("Unread fetch error:", err);
    }
  };

  React.useEffect(() => {
    if (!employeeId) return;

    fetchUnread();

    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, [employeeId]);

  const isAdmin = Number(employee?.privilages) === 2;

  // ✅ Base menu
  const baseNav = [
    { name: "Home", icon: HomeModernIcon, path: "home" },
    // { name: "Attendance", icon: ClockIcon, path: "attendance" },
    // { name: "Lead", icon: BookmarkIcon, path: "lead" },
    // { name: "Notifications", icon: BellIcon, path: "notifications" },
    // { name: "Assignments", icon: ClipboardDocumentCheckIcon, path: "assignments" },
    { name: "Enquiry Generation", icon: ClipboardDocumentCheckIcon, path: "ticketing" },

  ];

  // ✅ Admin menu
  const adminNav = [
    // { name: "Attendance Overview", icon: ClipboardDocumentListIcon, path: "attendance-overview" },
    // { name: "Leave Overview", icon: DocumentTextIcon, path: "leave-overview" },
    // { name: "Target vs Achievements", icon: ClipboardDocumentListIcon, path: "assign-tasks" },
    // { name: "Add Product", icon: BuildingOffice2Icon, path: "add-product" },
    // { name: "Statistics", icon: PresentationChartLineIcon, path: "statistics" },
    // { name: "Reports", icon: PresentationChartLineIcon, path: "reports" },
    // { name: "Add Employee", icon: UserGroupIcon, path: "add-employee" },
    // { name: "Generate Pay Slip", icon: CurrencyRupeeIcon, path: "generate-payslip" },
    // { name: "Expense History", icon: CurrencyRupeeIcon, path: "expense-history" }
  ];

  const NAV_ITEMS = isAdmin ? [...baseNav, ...adminNav] : baseNav;

  const handleNavigation = (path) => {
    // 🔥 SPECIAL CASE: notifications opens modal
    if (path === "notifications") {
      setShowNotifications(true);
      if (window.innerWidth < 1024 && onClose) onClose();
      return;
    }

    navigate(`/${path}`);
    if (window.innerWidth < 1024 && onClose) onClose();
  };


  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  return (
    <>
      {/* 🔥 Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        onClick={onClose}
      />

      {/* 🔥 Sidebar (hidden until opened) */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-[#0B1533] text-white shadow-xl
          transition-transform duration-300 flex flex-col
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* ✅ HEADER */}
        <div className="flex-shrink-0 h-16 px-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="bg-emerald-500/20 p-2 rounded-xl">
              <img
                src="/process_automation.png"
                alt="Company Logo"
                className="w-7 h-7 object-contain"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </div>
            <div className="text-sm font-semibold truncate">
              Process Automation
            </div>
          </div>

          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* ✅ SCROLLABLE MENU */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === `/${item.path}` ||
              location.pathname.startsWith(`/${item.path}/`);

            return (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.path)}
                className={`
    w-full flex items-center justify-between gap-4 p-3 rounded-xl transition-all duration-200
    ${isActive
                    ? "bg-emerald-500 text-white shadow-lg"
                    : "hover:bg-white/10 text-gray-200"
                  }
  `}
              >
                <div className="flex items-center gap-4">
                  <Icon className="w-6 h-6 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">
                    {item.name}
                  </span>
                </div>

                {/* 🔴 Notification Badge */}
                {item.path === "notifications" && unreadCount > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs font-semibold bg-red-500 text-white rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* ✅ FOOTER — Logged-in Employee */}
        <div className="flex-shrink-0 p-2 border-t border-white/10">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-sm font-bold">
              {(employee?.employee_name || "U").charAt(0)}
            </div>

            <div className="overflow-hidden">
              <div className="text-sm font-semibold truncate">
                {employee?.employee_name || "Employee"}
              </div>
              <div className="text-xs text-gray-300 truncate">
                {employee?.job_role || "Staff"}
              </div>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 p-3 rounded-xl text-red-300 hover:bg-red-500/10 transition"
          >
            <ArrowLeftOnRectangleIcon className="w-6 h-6" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {showNotifications && (
        <NotificationPanel
          employeeId={employeeId}
          onClose={() => setShowNotifications(false)}
          refreshCount={() => { }}
        />
      )}
    </>
  );
};

export default Sidebar;